import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { easeInOutQuart, easeOutExpo } from '@/lib/motion'
import { getCoverRadius, getLensRadius } from '@/lib/lens'
import { useIntro } from '@/components/generals/intro'
import { Iris, IRIS_OPEN } from '@/components/motion/iris'
import { Photo } from '@/components/motion/photo'
import { pick } from '@/content/photographs'
import { profile } from '@/content/profile'

import {
    animate,
    motion,
    useMotionTemplate,
    useMotionValue,
    useTransform
} from 'motion/react'

const photo = pick('black-stork-fishing')

const STORAGE_KEY = 'am-intro-seen'
const SHUTTER = ['1/60', '1/125', '1/250', '1/500', '1/1000', '1/2000']

type Stage = 'dark' | 'hunting' | 'locked' | 'captured' | 'handoff' | 'done'

const CAPTIONS: Record<Stage, string> = {
    dark: 'Hold still.',
    hunting: 'Waiting for the light',
    locked: 'Now.',
    captured: '',
    handoff: '',
    done: ''
}

const wait = (ms: number): Promise<void> => new Promise(resolve => window.setTimeout(resolve, ms))

const Corner: FC<{ className: string }> = ({ className }): ReactNode => {
    return <span className={cn('absolute size-6 border-bone/70 md:size-8', className)} />
}

export const ViewfinderIntro: FC = (): ReactNode => {
    const router = useRouter()
    const { finish } = useIntro()
    const [stage, setStage] = useState<Stage>('dark')
    const [shutter, setShutter] = useState(SHUTTER[1])
    const [meter, setMeter] = useState(-1.3)
    const [frames, setFrames] = useState(0)
    const skip = useRef(false)
    const closeNow = useRef<() => void>(() => undefined)

    const aperture = useMotionValue(0)
    const blur = useMotionValue(22)
    const flash = useMotionValue(0)
    const circle = useMotionValue(0)
    const photoOpacity = useMotionValue(1)
    const hud = useMotionValue(0)

    const filter = useMotionTemplate`blur(${blur}px)`
    const clipPath = useTransform(circle, radius => (radius > 0 ? `circle(${radius}px at 50% 50%)` : 'none'))

    useEffect(() => {
        let cancelled = false
        let ticker = 0
        const home = router.pathname === '/'
        const stopped = () => cancelled || skip.current

        const close = () => {
            window.clearInterval(ticker)

            if (cancelled) return

            aperture.set(IRIS_OPEN)
            hud.set(0)
            flash.set(0)
            photoOpacity.set(0)
            finish()

            try {
                window.sessionStorage.setItem(STORAGE_KEY, '1')
            }
            catch {
                // Storage can be blocked; the intro simply plays again.
            }

            if (!cancelled) setStage('done')
        }

        closeNow.current = () => {
            skip.current = true
            close()
        }

        const run = async () => {
            let seen = false

            try {
                seen = window.sessionStorage.getItem(STORAGE_KEY) === '1'
            }
            catch {
                seen = false
            }

            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

            if (seen || reduce) {
                photoOpacity.set(0)
                hud.set(0)
                finish()
                await animate(aperture, IRIS_OPEN, { duration: reduce ? 0.3 : 0.9, ease: easeOutExpo })
                if (!cancelled) setStage('done')
                return
            }

            animate(hud, 1, { duration: 0.8, delay: 0.15 })
            await wait(650)
            if (stopped()) return close()

            setStage('hunting')
            animate(aperture, window.innerWidth < 768 ? 22 : 15, { duration: 1.1, ease: easeOutExpo })
            animate(blur, [22, 8, 15, 4, 7, 0], { duration: 1.8, times: [0, 0.22, 0.42, 0.68, 0.84, 1], ease: 'easeInOut' })

            ticker = window.setInterval(() => {
                setShutter(SHUTTER[Math.floor(Math.random() * SHUTTER.length)])
                setMeter(Math.round((Math.random() * 3 - 1.5) * 3) / 3)
            }, 140)

            await wait(1850)
            window.clearInterval(ticker)
            if (stopped()) return close()

            setStage('locked')
            setShutter('1/2000')
            setMeter(0)
            await wait(520)
            if (stopped()) return close()

            await animate(aperture, 0, { duration: 0.12, ease: 'easeIn' })
            if (stopped()) return close()

            flash.set(0.92)
            setFrames(1)
            setStage('captured')
            animate(flash, 0, { duration: 0.9, ease: 'easeOut' })
            animate(hud, 0, { duration: 0.45 })
            await animate(aperture, IRIS_OPEN, { duration: 0.6, ease: easeOutExpo })
            await wait(380)
            if (stopped()) return close()

            setStage('handoff')

            if (home) {
                const width = document.documentElement.clientWidth || window.innerWidth
                const height = window.innerHeight

                circle.set(getCoverRadius(width, height))
                finish()
                await animate(circle, getLensRadius(width, height), { duration: 1.2, ease: easeInOutQuart })
            }
            else {
                finish()
                await animate(photoOpacity, 0, { duration: 0.7, ease: 'easeOut' })
            }

            close()
        }

        run()

        return () => {
            cancelled = true
            window.clearInterval(ticker)
        }
        // The intro runs once per page load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (stage === 'done') return null

    const locked = stage === 'locked' || stage === 'captured'
    const interactive = stage !== 'handoff'

    return (
        <div className={cn('fixed inset-0 z-[140] overflow-hidden', interactive ? 'pointer-events-auto' : 'pointer-events-none')}>
            <div aria-hidden='true' className='absolute inset-0'>
                <motion.div className='absolute inset-0' style={{ opacity: photoOpacity, clipPath }}>
                    <motion.div className='absolute -inset-6' style={{ filter }}>
                        <Photo photo={photo} fill preload sizes='100vw' />
                    </motion.div>
                </motion.div>
    
                <Iris aperture={aperture} />
    
                <motion.div className='absolute inset-0 bg-white' style={{ opacity: flash }} />
    
                <motion.div className='absolute inset-0 text-bone' style={{ opacity: hud }}>
                    <div className='absolute inset-[5vmin]'>
                        <Corner className='left-0 top-0 border-l border-t' />
                        <Corner className='right-0 top-0 border-r border-t' />
                        <Corner className='bottom-0 left-0 border-b border-l' />
                        <Corner className='bottom-0 right-0 border-b border-r' />
                    </div>
    
                    <div className='absolute inset-x-[calc(5vmin+18px)] top-[calc(5vmin+16px)] flex items-start justify-between font-mono text-[10px] uppercase tracking-[0.22em] md:text-[11px]'>
                        <span className='flex items-center gap-2'>
                            <span className={cn('size-1.5 rounded-full', locked ? 'bg-ember' : 'animate-pulse bg-bone/70')} />
                            AF-C
                        </span>
                        <span className='hidden text-bone/80 md:inline'>{profile.name} &mdash; Field archive</span>
                        <span className='text-bone/80'>{profile.coordinates}</span>
                    </div>
    
                    <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
                        <motion.div
                            className='relative size-16 md:size-20'
                            animate={locked
                                ? { scale: 0.82, x: 0, y: 0 }
                                : { scale: [1, 1.08, 0.96, 1.04, 1], x: [0, -3, 2, -1, 0], y: [0, 2, -2, 1, 0] }}
                            transition={locked
                                ? { type: 'spring', stiffness: 500, damping: 18 }
                                : { duration: 0.7, repeat: Infinity, ease: 'easeInOut' }}
                        >
                            {['left-0 top-0 border-l border-t', 'right-0 top-0 border-r border-t', 'bottom-0 left-0 border-b border-l', 'bottom-0 right-0 border-b border-r'].map(position => (
                                <span
                                    key={position}
                                    className={cn('absolute size-3.5 border-[1.5px] transition-colors duration-200', position, locked ? 'border-ember' : 'border-bone')}
                                />
                            ))}
                            <span className={cn('absolute left-1/2 top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors', locked ? 'bg-ember' : 'bg-transparent')} />
                        </motion.div>
                    </div>
    
                    <div className='absolute inset-x-0 top-[calc(50%+4.5rem)] text-center md:top-[calc(50%+5.5rem)]'>
                        <motion.p
                            key={stage}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45 }}
                            className='font-heading text-[clamp(22px,2.4vw,34px)] italic text-bone'
                        >
                            {CAPTIONS[stage]}
                        </motion.p>
                    </div>
    
                    <div className='absolute inset-x-[calc(5vmin+18px)] bottom-[calc(5vmin+16px)] grid grid-cols-2 items-end gap-4 font-mono text-[10px] uppercase tracking-[0.2em] md:grid-cols-3 md:text-[11px]'>
                        <div className='flex items-baseline gap-4 tabular-nums'>
                            <span className={cn('text-[18px] tracking-[0.08em] md:text-[22px]', locked ? 'text-ember' : 'text-bone')}>
                                {shutter}
                            </span>
                            <span>F5.6</span>
                            <span className='hidden sm:inline'>ISO 800</span>
                        </div>
    
                        <div className='hidden flex-col items-center gap-2 md:flex'>
                            <div className='relative flex w-56 justify-between text-[9px] text-bone/70'>
                                <span>-2</span>
                                <span>-1</span>
                                <span>0</span>
                                <span>+1</span>
                                <span>+2</span>
                            </div>
                            <div className='relative h-2 w-56 border-x border-bone/40'>
                                <span className='absolute inset-x-0 top-1/2 h-px bg-bone/30' />
                                <span
                                    className='absolute top-0 h-2 w-[3px] -translate-x-1/2 bg-ember transition-[left] duration-150'
                                    style={{ left: `${((meter + 2) / 4) * 100}%` }}
                                />
                            </div>
                        </div>
    
                        <div className='flex items-center justify-end gap-5'>
                            <span className='tabular-nums text-bone/80'>[{String(frames).padStart(3, '0')}]</span>
                        </div>
                    </div>
                </motion.div>
            </div>

            {interactive && (
                <button
                    type='button'
                    onClick={() => closeNow.current()}
                    className='pointer-events-auto absolute bottom-[calc(5vmin+48px)] right-[calc(5vmin+18px)] font-mono text-[10px] uppercase tracking-[0.22em] text-bone/60 transition-colors hover:text-bone md:bottom-[calc(5vmin+52px)]'
                >
                    Skip intro &rarr;
                </button>
            )}
        </div>
    )
}
